import React from 'react';
import { buildColumnsParam, canHideColumn, unloadedVisibleColumns, unrequestedPayloadColumns } from './columns';
import { ADD_REASONING_LABEL, loadColumnsLabel } from './copy';
import { isMockFeedKey } from './mockFeedKey';
import { fetchPayloadFields, knownPayloadFieldsFor } from './specPayloadFields';

// Everything the page knows about which COLUMNS a feed will give it, and what to ask for next time.
//
// Two hundred and sixty contiguous lines of the page rendered nothing: three per-feed caches, a
// request builder with three tick-sensitive escape hatches, and four derived gates. None of it is
// layout, and all of it is why a redesign that moves the column chooser has to understand the
// `columns` wire protocol first.
//
// THE PROTOCOL, since it is the thing that makes this hard: `columns` is ADDITIVE. Omitting it asks
// for the floor alone; naming a column adds it. There is no drop operation, so this sends a
// keep-list on every read. A name outside the feed's own universe is a 422 on the WHOLE read, which
// is why every cache here is per-feed and why a parent's index-flag names must never reach a
// member's request.
//
// A pure function of read state — `{ result, displayedFeedId, failure, lastRunFeedId }` — so it can
// be tested without a page and moved without a layout decision.

// One shared reference per "nothing here", so the common case doesn't hand the table and the
// chooser a brand-new array on every render.
const NO_HIDDEN_COLUMNS = [];
const NO_REFUSED_COLUMNS = [];
const NO_PAYLOAD_FIELDS = [];
const NO_AWAITED_COLUMNS = [];

export const useColumnNegotiation = ({ result, displayedFeedId, failure, lastRunFeedId }) => {
  // Computed once rather than asked three times. A fixture is nobody's feed: it files no universe,
  // caches no refusal, and has no server to ask again — so every gate that turns on "is this a real
  // feed" turns on this.
  const isFixture = isMockFeedKey(displayedFeedId);

  // Which optional columns are hidden, held per feed. Per feed because `columns` is additive and
  // scoped to one feed's universe: a name that feed doesn't have is a 422 on the whole read, not a
  // column quietly missing from the table. A single global hidden set therefore doesn't merely show
  // the wrong columns after a drill-down — it sends the parent's column names to the member and
  // fails the member's read outright.
  const [columnsByFeed, setColumnsByFeed] = React.useState({});

  // Each feed's own column universe, as reported by the `available_columns` of a response we have
  // already seen. In memory only, deliberately: read #1 of an unseen feed can only guess at the
  // universe, and the point of this map is that read #2 in the same session doesn't have to.
  const [availableColumnsByFeed, setAvailableColumnsByFeed] = React.useState({});

  // Feeds that answered a `columns` request by refusing it — a FastAPI `extra: forbid` deployment
  // rejects the key itself, and the only real capture of this endpoint comes from such a server.
  // viewFeed hides that behind a retry without `columns`, so the read succeeds and nothing looks
  // wrong; what it costs is TWO full ~24-second engine passes, on every read, forever, because the
  // page had no memory of the refusal and guessed again next time. Kept apart from
  // availableColumnsByFeed on purpose: "this feed rejects the key" and "this feed's universe is
  // empty" are different facts, and overloading [] to carry both is how the first one gets lost.
  const [columnsRefusedByFeed, setColumnsRefusedByFeed] = React.useState({});

  // Each feed's payload column names, resolved from the score spec that published it.
  //
  // A REF, not state, and that is the whole point. Payload names are needed to BUILD a request, and
  // the request goes out in the same tick the names arrive — a setState would not be visible to that
  // closure, which is the trap `ignoreRefusal` and `slim` already exist to route around. Nothing
  // renders from this, so a ref costs nothing and removes the tick entirely.
  //
  // The value is an array of names, or `null` meaning "asked, and the spec answered that there are
  // none" — a combined feed, a free-form feed_name, a spec that declares no extra_columns. That is
  // cached, because re-asking a settled question costs a request per drill-down for nothing. A FAILED
  // fetch is not cached: a 500 is not an answer, and caching one would disable this route for the
  // feed for the rest of the session over a blip.
  const payloadFieldsRef = React.useRef({});

  // Which feeds have already spent their one bootstrap re-read, so the fallback below can never loop.
  const bootstrappedFeedsRef = React.useRef({});

  // And which have spent their one automatic re-read.
  const autoLoadedFeedsRef = React.useRef({});

  const hiddenColumns = columnsByFeed[displayedFeedId] || NO_HIDDEN_COLUMNS;

  // A result object is filed once. Without this, any later render that changed only the pending feed
  // would re-file the same result under a different id.
  const filedResultRef = React.useRef(null);
  React.useEffect(() => {
    if (!result || result === filedResultRef.current) return;
    filedResultRef.current = result;
    // A mock fixture is nobody's feed, so it files nothing about a feed: no universe, no refusal.
    // It still keys the display, which is why it has a key at all.
    if (!displayedFeedId || isFixture) return;
    // What the server named when it refused, kept beside the refusal itself. Read 2 onwards sends no
    // `columns`, so no later response can carry these names — the cache is the only place they can
    // come from once the page stops asking. Empty is a refusal that named nothing, not the absence
    // of a refusal: an array is truthy either way, which is what the readers below turn on.
    if (result.droppedColumns) {
      setColumnsRefusedByFeed((prev) => ({ ...prev, [displayedFeedId]: result.refusedColumns || NO_REFUSED_COLUMNS }));
    }
    // An empty universe is a server that said nothing, not a feed with no columns, so it must not
    // overwrite what an earlier read of this feed did say. Same rule columns.js applies to [].
    if (!result.availableColumns || !result.availableColumns.length) return;
    // A SLIM read's universe is not this feed's universe. Slim empties the spec's payload keys
    // server-side, so `available_columns` comes back without them — and filing that as the feed's
    // universe would teach the page that this feed has no explanation and no thinking, permanently
    // for the session. The next non-slim read would then ask for neither and the analyst would find
    // the reasoning gone with nothing on screen to explain it. Better to keep guessing: the guess
    // (SPECULATIVE_COLUMNS) is slim-safe by construction and a non-slim read will file the real
    // universe the moment one happens.
    if (result.slim) return;
    setAvailableColumnsByFeed((prev) => ({ ...prev, [displayedFeedId]: result.availableColumns }));
  }, [result, displayedFeedId]);

  // A read whose floor-only retry also failed still proved this feed refuses the column request, and
  // that is the one refusal the success path above can never file: it returns no rows, so no result
  // lands and the filing effect never runs. Without this the feed pays the full 422-then-retry double
  // pass on every read for the rest of the session, and the analyst reads a generic three-cause 422
  // with nothing saying columns were involved. Keyed on the feed the failed run was actually FOR —
  // not on the displayed feed, which a failed drill-down has already rolled back to the parent.
  React.useEffect(() => {
    if (!failure?.columnsImplicated) return;
    const feedId = lastRunFeedId();
    if (!feedId || isMockFeedKey(feedId)) return;
    setColumnsRefusedByFeed((prev) => ({ ...prev, [feedId]: failure.refusedColumns || NO_REFUSED_COLUMNS }));
  }, [failure, lastRunFeedId]);

  const toggleColumn = React.useCallback(
    (key) =>
      setColumnsByFeed((prev) => {
        const hidden = prev[displayedFeedId] || NO_HIDDEN_COLUMNS;
        if (hidden.includes(key)) return { ...prev, [displayedFeedId]: hidden.filter((k) => k !== key) };
        // A table with no columns is a blank rectangle.
        if (!canHideColumn(result, hidden, key)) return prev;
        return { ...prev, [displayedFeedId]: [...hidden, key] };
      }),
    [displayedFeedId, result]
  );

  // The `columns` to send for one feed, built from that feed's hidden set and that feed's known
  // universe — and the only place any of the three run call sites is allowed to build it. Each used
  // to assemble the list from whatever result happened to be on screen, which is how a member feed's
  // request came to carry the parent's is_member_of_* names: names the member has never heard of.
  //
  // `ignoreRefusal` is the analyst pressing "ask for the columns again" on the notice. The refusal is
  // cleared in that same handler, but a setState is not visible to this closure in the tick that
  // schedules it, so the caller has to say so directly — reading `columnsRefusedByFeed` there would
  // read the value from before the clear and send the floor-only request the press exists to escape.
  // `slim` is threaded in rather than read off `formState`, for the same reason the run params are:
  // a submit resolves its own state in one tick (the company filter's pending text), and a recent-run
  // press runs the patch it just built rather than the form it is about to set. Reading the form here
  // would send the PREVIOUS read's slim setting with this read's columns.
  const requestColumnsFor = React.useCallback(
    (feedId, { ignoreRefusal = false, slim = false, payloadFields } = {}) => {
      // This feed has already answered a `columns` request by rejecting the key. Sending it again
      // buys the same two round trips — the 422, then viewFeed's retry without `columns` — for
      // exactly the same rows, every read, forever. That is the cost this cache exists to remove, so
      // it is checked FIRST and unconditionally.
      //
      // In particular it is not conditioned on the universe being unknown. A server can publish
      // `available_columns` in the RESPONSE and still have `extra: forbid` on the REQUEST model —
      // a half-rolled-out deploy is enough — and then read 1's retry files a universe, which under
      // the old guard permanently switched this cache back off and restored the doubled read it was
      // written to end. A refusal of the key is a fact about the key, not about the universe.
      if (!ignoreRefusal && columnsRefusedByFeed[feedId]) {
        return { columns: undefined };
      }

      const filed = availableColumnsByFeed[feedId];
      // [] is UNKNOWN, not "this feed has no columns" — the rule columns.js states and the reason
      // this isn't `filed || null`. An empty array is truthy, so the old form called a guessed
      // keep-list authoritative, which used to switch off the retry that exists to rescue exactly
      // that read.
      const availableColumns = Array.isArray(filed) && filed.length ? filed : null;

      // Index flag names are unguessable, so against a server that hasn't shipped
      // `available_columns` the only place they can come from is a previous read's own rows — which
      // makes them requestable at all, and the Indices toggle something other than inert. Strictly
      // when the rows on screen ARE this feed's: harvested from any other feed they are names this
      // one has never heard of, and a name outside the universe fails the whole read.
      const indexFields = displayedFeedId === feedId ? result?.indexFields : null;

      // A `speculativeColumns: !availableColumns` flag used to ride along here, saying the keep-list
      // was a guess. viewFeed stopped reading it when its retry narrowed to gate on the server naming
      // the `columns` field, and nothing else ever did — so it was a value computed on every read to
      // claim an influence it no longer had.

      // Passed in by a caller that has just resolved them, else whatever a previous resolve filed.
      // The explicit argument matters for the same reason `ignoreRefusal` does: the drill-down
      // resolves the spec and builds the request in one tick, and a caller that had to wait for the
      // cache to be readable would send the request the resolve exists to prevent.
      const payloadNames = payloadFields === undefined ? payloadFieldsRef.current[feedId] : payloadFields;

      return {
        columns: buildColumnsParam({
          availableColumns,
          hiddenKeys: columnsByFeed[feedId] || NO_HIDDEN_COLUMNS,
          indexFields,
          payloadFields: payloadNames || NO_PAYLOAD_FIELDS,
          // A slim read's universe is narrower than the one filed above: the server empties this
          // feed's payload keys, so naming one is a 422 the page then caches as a refusal of the
          // whole `columns` key — costing every later NON-slim read its enrichment. See the note in
          // buildColumnsParam.
          slim,
        }),
      };
    },
    [availableColumnsByFeed, columnsByFeed, columnsRefusedByFeed, displayedFeedId, result]
  );

  // The payload column names for a feed, from the score spec that published it — the A2 route.
  //
  // Why the spec and not the response: `columns` is additive, so a payload column arrives only if the
  // request named it, and read #1 of an unseen feed has nothing to name it from. A drill-down is
  // ALWAYS read #1 of the member's feed, and never gets a read #2 — the form still points at the
  // parent, so Submit re-reads the parent — which is why the reasoning disclosure went missing there
  // and only there. The feed id carries its spec id (specForFeed.js), so the names are one cheap GET
  // away, before the ~24s read goes out.
  //
  // FAILS OPEN in every direction. An id that names no spec, a spec that declares nothing, a fetch
  // that errors — all yield null, the request falls back to the guess it makes today, and the A1
  // bootstrap in openMemberFeed is what catches it.
  //
  // Split across two functions, and the split is load-bearing: only a feed whose spec has NOT been
  // looked up yet may cost a round trip, and therefore a tick, before the read goes out. Everything
  // else — a combined feed, a free-form feed_name, a spec already read this session — answers
  // synchronously, so a drill-down into one is still issued in the same tick as the press.

  // What is already known, without asking anyone. `undefined` is the third state and the only reason
  // the caller can stay synchronous: not "no payload" but "nobody has asked yet".
  const knownPayloadFields = React.useCallback((feedId) => {
    const cached = payloadFieldsRef.current[feedId];
    if (cached !== undefined) return cached;

    // A settled answer — a combined feed, a free-form feed_name — is cached rather than re-derived
    // per press. `undefined` means nobody has asked yet, and is what keeps the caller synchronous.
    const settled = knownPayloadFieldsFor(feedId);
    if (settled !== undefined) payloadFieldsRef.current[feedId] = settled;
    return settled;
  }, []);

  // And the asking part: one GET, once per feed per session.
  const resolvePayloadFields = React.useCallback(
    async (feedId) => {
      const known = knownPayloadFields(feedId);
      if (known !== undefined) return known;

      const { resolved, cacheable } = await fetchPayloadFields(feedId);
      // A failed fetch is not cached — see the note on the ref. The read still goes out; it just
      // goes out guessing.
      if (cacheable) payloadFieldsRef.current[feedId] = resolved;
      return resolved;
    },
    [knownPayloadFields]
  );

  // What this feed said when it refused the `columns` key, or undefined if it never has. The value is
  // the names it blamed — possibly none — so the presence of an entry is the refusal and the contents
  // are only what the notice gets to say about it.
  const cachedRefusal = columnsRefusedByFeed[displayedFeedId];

  // True while the rows on screen belong to a feed known to reject the `columns` key — either
  // because THIS response is the one that discovered it, or because an earlier read did and the
  // negative cache has been quietly asking for the floor ever since.
  const columnsRefused = !!result?.droppedColumns || !!cachedRefusal;

  // This feed says it has reasoning columns and these rows did not ask for them — the silent gap,
  // which rendered as nothing at all: no notice, no error, just a missing disclosure indistinguishable
  // from a score spec that declares none.
  const missingPayload = result ? unrequestedPayloadColumns(result).length > 0 : false;

  // The OTHER gap, and the one that had no button at all: a column this feed declares, that nothing
  // turned off, that these rows still don't carry. Index flags are the case that matters, and they
  // are unreachable on read #1 by construction — the names are feed-specific and `columns` is
  // validated against a universe the page can only learn from a response. So read #1 shows the
  // Indices toggle on, no Indices column, and no index filter, and the only remedy the page offered
  // was a sentence telling the analyst to run the whole ~24-second read again themselves.
  //
  // It was excluded from the button on the premise that these columns can only be absent by choice.
  // That premise holds from read #2 onwards and is false exactly when it matters.
  const awaitedColumns = result ? unloadedVisibleColumns(result, hiddenColumns) : NO_AWAITED_COLUMNS;

  // Not offered while `columnsRefused`: that feed rejects the column request outright, so the press
  // could not deliver, and the notice below already explains it and offers the read that might.
  const canLoadColumns =
    !columnsRefused && !!displayedFeedId && !isFixture && (missingPayload || awaitedColumns.length > 0);

  const loadColumnsButtonLabel = missingPayload
    ? ADD_REASONING_LABEL
    : loadColumnsLabel(awaitedColumns.map((column) => column.label));

  // Prefer the names off THIS response: it is the read that was actually refused, and on the frame it
  // lands the cache is still one effect behind it. Every later read of the same feed sends no
  // `columns` at all, so it has no names of its own and the cache is the only record left.
  const refusedColumnNames = (result?.droppedColumns && result.refusedColumns) || cachedRefusal || NO_REFUSED_COLUMNS;

  // Reset for a page the analyst has taken back to the start.
  //
  // columnsRefusedByFeed deliberately SURVIVES. A server that rejects the `columns` key rejects it
  // whatever this page has on screen, so forgetting the refusal here would only buy the doubled
  // round trip back on the next read.
  const resetColumns = React.useCallback(() => {
    setColumnsByFeed({});
    setAvailableColumnsByFeed({});
    autoLoadedFeedsRef.current = {};
  }, []);

  // The analyst pressing "ask for the columns again" on the refusal notice.
  const forgetRefusal = React.useCallback((feedId) => {
    setColumnsRefusedByFeed((prev) => {
      const next = { ...prev };
      delete next[feedId];
      return next;
    });
  }, []);

  // Once per feed per page. The comparison that drives the automatic re-read cannot see a CANCEL —
  // the read is aborted, no result lands, and every input to it is what it was a moment ago — so
  // without this, cancelling the automatic read starts the automatic read.
  const claimAutoLoad = React.useCallback((feedId) => {
    if (autoLoadedFeedsRef.current[feedId]) return false;
    autoLoadedFeedsRef.current[feedId] = true;
    return true;
  }, []);

  // Same shape, for the one bootstrap re-read a member feed may spend.
  const claimBootstrap = React.useCallback((feedId) => {
    if (bootstrappedFeedsRef.current[feedId]) return false;
    bootstrappedFeedsRef.current[feedId] = true;
    return true;
  }, []);

  const hiddenColumnsFor = React.useCallback((feedId) => columnsByFeed[feedId] || NO_HIDDEN_COLUMNS, [columnsByFeed]);

  return {
    // Exposed rather than re-derived by every caller that has to ask it.
    isFixture,
    hiddenColumns,
    hiddenColumnsFor,
    toggleColumn,
    requestColumnsFor,
    knownPayloadFields,
    resolvePayloadFields,
    columnsRefused,
    refusedColumnNames,
    missingPayload,
    awaitedColumns,
    canLoadColumns,
    loadColumnsButtonLabel,
    isRefused: (feedId) => !!columnsRefusedByFeed[feedId],
    resetColumns,
    forgetRefusal,
    claimAutoLoad,
    claimBootstrap,
  };
};
